import { ref } from 'vue';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { projectAuth, projectFunctions } from '@/firebase/firebase';

const KEY = 'llf.adminSession.v2';
const manage = httpsCallable(projectFunctions, 'manageAdminSession');
export const sessionMfaRequired = ref(false);
export const sessionBlocked = ref(true);
export const sessionPrompt = ref(false);
export const sessionSeconds = ref(180);
export const sessionError = ref('');
let generation = 0;
let active = false;
let loggingOut = false;
let deadline = 0;
let renewUntil = 0;
let serverTime = 0;
let receivedAt = 0;
let renewalPending = false;
const time = () => serverTime + performance.now() - receivedAt;
const notifyTabs = () => {
  try { localStorage.setItem(KEY, `${Date.now()}-${Math.random()}`); } catch { /* Auth sign-out also synchronizes tabs. */ }
};
function apply(data) {
  sessionMfaRequired.value = data.mfaRequired === true;
  if (sessionMfaRequired.value) {
    deadline = 0; renewUntil = 0;
    sessionBlocked.value = true; sessionPrompt.value = false; sessionError.value = '';
    return;
  }
  deadline = data.deadline;
  renewUntil = data.renewUntil;
  serverTime = data.serverNow;
  receivedAt = performance.now();
  sessionError.value = '';
  checkSession();
}
export async function endAdminSession() {
  sessionBlocked.value = true;
  sessionPrompt.value = false;
  if (loggingOut) return;
  loggingOut = true;
  try {
    try { await manage({ action: 'end' }); } catch { /* Local logout must work offline too. */ }
    notifyTabs();
    await signOut(projectAuth);
    window.location.replace('/');
  } catch {
    sessionError.value = 'Unable to sign out. Please retry.';
  } finally { loggingOut = false; }
}
function checkSession() {
  if (!active || loggingOut || !deadline) return;
  if (time() >= renewUntil) { void endAdminSession(); return; }
  sessionPrompt.value = time() >= deadline;
  sessionBlocked.value = sessionPrompt.value;
  sessionSeconds.value = Math.max(0, Math.ceil((renewUntil - time()) / 1000));
}
async function syncSession() {
  if (!active || loggingOut) return;
  const current = generation;
  // Remove protected content until the server confirms access after wake/focus.
  if (!deadline || time() >= deadline) sessionBlocked.value = true;
  try {
    const response = await manage({ action: 'start' });
    if (current === generation && active && !loggingOut) apply(response.data);
  } catch {
    if (current === generation) void endAdminSession();
  }
}
export async function renewAdminSession() {
  if (!active || loggingOut || renewalPending) return;
  renewalPending = true;
  const current = generation;
  try {
    const response = await manage({ action: 'renew' });
    if (current === generation && active && !loggingOut) { apply(response.data); notifyTabs(); }
  } catch {
    if (current === generation) void endAdminSession();
  } finally { renewalPending = false; }
}
export function startAdminSessionController() {
  const unsubscribe = onAuthStateChanged(projectAuth, user => {
    generation++;
    active = Boolean(user);
    deadline = 0;
    sessionBlocked.value = active;
    sessionPrompt.value = false;
    sessionError.value = '';
    sessionMfaRequired.value = false;
    if (active) void syncSession();
  });
  const timer = setInterval(checkSession, 1000);
  const poll = setInterval(() => { if (!document.hidden) void syncSession(); }, 60000);
  const changed = event => { if (event.key === KEY || event.key === null) void syncSession(); };
  const visible = () => { if (!document.hidden) void syncSession(); };
  window.addEventListener('storage', changed);
  window.addEventListener('focus', visible);
  window.addEventListener('pageshow', visible);
  document.addEventListener('visibilitychange', visible);
  return () => {
    generation++; active = false; unsubscribe(); clearInterval(timer); clearInterval(poll);
    window.removeEventListener('storage', changed);
    window.removeEventListener('focus', visible);
    window.removeEventListener('pageshow', visible);
    document.removeEventListener('visibilitychange', visible);
  };
}
